import InvoiceFooter from "Components/Common/PdfPreview/InvoiceFooter";
import ItemsTable from "Components/Common/PdfPreview/ItemList";
import SenderAndBillingDetails from "Components/Common/PdfPreview/SenderAndBillingDetails";
import { ICONS } from "config/assetPaths";
import { useMemo } from "react";
import { Col } from "reactstrap";
import { CompanyPayload } from "types/auth.api";
import { ClientsPayloadData } from "types/client.api";
import { ItemData } from "types/item.api";
import { SectionPayloadData } from "types/section.api";
import parse from "html-react-parser";

interface Props {
  companyDetails?: CompanyPayload | null;
  clientDetails?: ClientsPayloadData | null;
  selectedOption?: string;
  issueDate?: string;
  dueDate?: string;
  tableData?: ItemData[];
  referenceNumber?: string;
  selectedTab?: string;
  jobDescription?: string;
  allSelectedSections?: SectionPayloadData[];
  quoteTitle?: string;
  isCompanyPhoneHidden?: boolean;
  isCompanyNameHidden?: boolean;
}

const TemplateThird = ({
  clientDetails,
  companyDetails,
  selectedOption = "items",
  dueDate,
  issueDate,
  tableData = [],
  referenceNumber,
  selectedTab,
  jobDescription,
  allSelectedSections,
  quoteTitle,
  isCompanyPhoneHidden,
  isCompanyNameHidden,
}: Props) => {
  // -----------------------
  //  COLUMNS PER VIEW TYPE
  // -----------------------
  const columns = useMemo(() => {
    switch (selectedOption) {
      case "by-item":
        return [
          {
            header: "Item",
            accessorKey: "name",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Unit",
            accessorKey: "unit",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Qty",
            accessorKey: "quantity",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Unit Price",
            accessorKey: "price",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const value = Number(cell.getValue() ?? 0);
              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{value.toFixed(2)}
                </span>
              );
            },
          },
          {
            header: "Total",
            accessorKey: "total",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const row = cell.row.original as ItemData;
              const qty = Number(row.quantity ?? 0);
              const price = Number(row.price ?? 0);
              const total = qty * price;

              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{total.toFixed(2)}
                </span>
              );
            },
          },
        ];

      case "by-category":
        return [
          {
            header: "Category",
            accessorKey: "category",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue() ? cell.getValue() : "-"}
              </span>
            ),
          },
          {
            header: "Items",
            accessorKey: "itemsCount",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Total",
            accessorKey: "total",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const value = Number(cell.getValue() ?? 0);
              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{value.toFixed(2)}
                </span>
              );
            },
          },
        ];

      case "by-subcategory":
        return [
          {
            header: "Subcategory",
            accessorKey: "subcategory",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue() ? cell.getValue() : "-"}
              </span>
            ),
          },
          {
            header: "Items",
            accessorKey: "itemsCount",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Total",
            accessorKey: "total",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const value = Number(cell.getValue() ?? 0);
              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{value.toFixed(2)}
                </span>
              );
            },
          },
        ];

      case "by-category-subcategory-item":
      default:
        return [
          {
            header: "Subcategory",
            accessorKey: "subcategory",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue() ? cell.getValue() : "-"}
              </span>
            ),
          },
          {
            header: "Item",
            accessorKey: "item",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Unit",
            accessorKey: "unit",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Qty",
            accessorKey: "qty",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },

          {
            header: "Unit Price",
            accessorKey: "unitPrice",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const value = Number(cell.getValue() ?? 0);
              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{value.toFixed(2)}
                </span>
              );
            },
          },
          {
            header: "Total",
            accessorKey: "total",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const value = Number(cell.getValue() ?? 0);
              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{value.toFixed(2)}
                </span>
              );
            },
          },
        ];
    }
  }, [selectedOption]);

  // -----------------------------------
  //  GROUPING / SUMMARY DATA PER MODE
  // -----------------------------------
  const { categoryViewData, subcategoryViewData, allViewData } = useMemo(() => {
    const result = {
      categoryViewData: [] as {
        category: string;
        itemsCount: number;
        total: number;
      }[],
      subcategoryViewData: [] as {
        subcategory: string;
        itemsCount: number;
        total: number;
      }[],
      allViewData: [] as {
        categoryId: string | number;
        category: string;
        rows: any[];
      }[],
    };

    if (!Array.isArray(tableData)) return result;

    const categoryMap = new Map<
      string | number,
      {
        categoryId: string | number;
        category: string;
        subMap: Map<
          string | number,
          {
            subcategoryId: string | number;
            subcategory: string;
            itemMap: Map<
              string,
              {
                name: string;
                qty: number;
                price: number;
                total: number;
                unit: string;
              }
            >;
          }
        >;
      }
    >();

    tableData.forEach((item) => {
      const categoryId = item.category_id ?? "na";
      const categoryName = item.category_name ?? "-";

      const subId = item.subcategory_id ?? "na";
      const subName = item.subcategory_name ?? "-";

      if (!categoryMap.has(categoryId)) {
        categoryMap.set(categoryId, {
          categoryId,
          category: categoryName,
          subMap: new Map(),
        });
      }

      const categoryData = categoryMap.get(categoryId)!;

      if (!categoryData.subMap.has(subId)) {
        categoryData.subMap.set(subId, {
          subcategoryId: subId,
          subcategory: subName,
          itemMap: new Map(),
        });
      }

      const subData = categoryData.subMap.get(subId)!;

      const itemKey = item.name?.toLowerCase() ?? "unknown-item";
      const qty = Number(item.quantity ?? 0);
      const price = Number(item.price ?? 0);

      if (!subData.itemMap.has(itemKey)) {
        subData.itemMap.set(itemKey, {
          name: item.name,
          qty,
          price,
          total: qty * price,
          unit: item.unit,
        });
      } else {
        const exist = subData.itemMap.get(itemKey)!;
        exist.qty += qty;
        exist.total += qty * price;
      }
    });

    const categoryArray = Array.from(categoryMap.values());

    // CATEGORIES summary
    let catTemp = categoryArray.map((cat) => {
      const subItems = Array.from(cat.subMap.values()).flatMap((sub) =>
        Array.from(sub.itemMap.values()),
      );
      return {
        category: cat.category,
        itemsCount: subItems.reduce((s, i) => s + i.qty, 0),
        total: subItems.reduce((s, i) => s + i.total, 0),
      };
    });

    // Merge - Categories
    const catNA = catTemp.filter((c) => c.category === "-");
    const catValid = catTemp.filter((c) => c.category !== "-");

    if (catNA.length > 0) {
      const merged = catNA.reduce(
        (acc, c) => {
          acc.itemsCount += c.itemsCount;
          acc.total += c.total;
          return acc;
        },
        { category: "-", itemsCount: 0, total: 0 },
      );
      result.categoryViewData = [...catValid, merged];
    } else {
      result.categoryViewData = catValid;
    }

    // SUBCATEGORY summary
    let subTemp = categoryArray.flatMap((cat) =>
      Array.from(cat.subMap.values()).map((sub) => {
        const items = Array.from(sub.itemMap.values());
        return {
          subcategory: sub.subcategory,
          itemsCount: items.reduce((s, i) => s + i.qty, 0),
          total: items.reduce((s, i) => s + i.total, 0),
        };
      }),
    );

    // Merge - Subcategories
    const subNA = subTemp.filter((s) => s.subcategory === "-");
    const subValid = subTemp.filter((s) => s.subcategory !== "-");

    if (subNA.length > 0) {
      const mergedSub = subNA.reduce(
        (acc, s) => {
          acc.itemsCount += s.itemsCount;
          acc.total += s.total;
          return acc;
        },
        { subcategory: "-", itemsCount: 0, total: 0 },
      );
      result.subcategoryViewData = [...subValid, mergedSub];
    } else {
      result.subcategoryViewData = subValid;
    }

    // ALL view (Category → Subcategory → Items)
    result.allViewData = categoryArray.map((cat) => ({
      categoryId: cat.categoryId,
      category: cat.category,
      rows: Array.from(cat.subMap.values()).flatMap((sub) =>
        Array.from(sub.itemMap.values()).map((i) => ({
          subcategory: sub.subcategory,
          item: i.name,
          qty: i.qty,
          unitPrice: i.price,
          total: i.total,
          unit: i.unit,
        })),
      ),
    }));

    return result;
  }, [tableData]);

  // decide data for non-"all" views
  const mainTableData = useMemo(() => {
    switch (selectedOption) {
      case "by-category":
        return categoryViewData;
      case "by-subcategory":
        return subcategoryViewData;
      case "by-item":
      default:
        return tableData;
    }
  }, [selectedOption, categoryViewData, subcategoryViewData, tableData]);

  const brandColor = companyDetails?.brand_color ?? "#082B60";

  return (
    <Col
      className="position-relative p-0"
      style={{
        cursor: "pointer",
        borderRadius: "12px",
        minHeight: "100vh", // or set a specific height
      }}
    >
      {/* Background Image */}
      <img
        src={ICONS.quote_blank_template3}
        alt="template"
        className="position-absolute top-0 start-0 w-100 h-100"
        style={{
          objectFit: "cover",
          borderRadius: "12px",
          zIndex: 0,
        }}
      />

      {/* Content Overlay Wrapper */}
      <div
        className="position-relative d-flex flex-column"
        style={{
          minHeight: "100vh", // or match your desired height
          zIndex: 1,
        }}
      >
        {/* Main Content - Now scrollable */}
        <div className="flex-grow-1 mx-3 overflow-auto overflow-x-hidden">
          {selectedTab === "1" && (
            <>
              <SenderAndBillingDetails
                isBillingDetails={false}
                colorName={brandColor}
                companyDetails={companyDetails}
                referenceNumber={referenceNumber}
                quoteName={quoteTitle || ""}
                isCompanyPhoneHidden={isCompanyPhoneHidden}
                isCompanyNameHidden={isCompanyNameHidden}
              />

              <svg
                className="w-100  my-4"
                viewBox="0 0 351 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_6314_4743)">
                  <path
                    d="M240.53 5.39459H240.747C240.767 5.39459 240.757 5.42675 240.722 5.4672L234.478 12.9114C234.445 12.9519 234.401 12.9851 234.382 12.9851H234.165C234.144 12.9851 234.156 12.9519 234.19 12.9114L240.433 5.4672C240.467 5.42675 240.511 5.39459 240.53 5.39459ZM238.435 5.39459H238.652C238.672 5.39459 238.66 5.42675 238.627 5.4672L232.383 12.9114C232.35 12.9519 232.306 12.9851 232.285 12.9851H232.069C232.049 12.9851 232.06 12.9519 232.095 12.9114L238.338 5.4672C238.372 5.42675 238.416 5.39459 238.435 5.39459ZM236.34 5.39459H236.557C236.576 5.39459 236.565 5.42675 236.532 5.4672L230.288 12.9114C230.254 12.9519 230.21 12.9851 230.19 12.9851H229.973C229.954 12.9851 229.965 12.9519 229.998 12.9114L236.243 5.4672C236.277 5.42675 236.32 5.39459 236.34 5.39459ZM234.245 5.39459H234.462C234.481 5.39459 234.47 5.42675 234.436 5.4672L228.193 12.9114C228.158 12.9519 228.115 12.9851 228.095 12.9851H227.878C227.859 12.9851 227.87 12.9519 227.903 12.9114L234.147 5.4672C234.18 5.42675 234.224 5.39459 234.245 5.39459ZM232.149 5.39459H232.365C232.386 5.39459 232.375 5.42675 232.34 5.4672L226.097 12.9114C226.063 12.9519 226.02 12.9851 226 12.9851H225.783C225.763 12.9851 225.774 12.9519 225.808 12.9114L232.052 5.4672C232.085 5.42675 232.129 5.39459 232.149 5.39459ZM230.053 5.39459H230.27C230.29 5.39459 230.279 5.42675 230.245 5.4672L224.002 12.9114C223.968 12.9519 223.924 12.9851 223.905 12.9851H223.688C223.667 12.9851 223.679 12.9519 223.713 12.9114L229.956 5.4672C229.99 5.42675 230.034 5.39459 230.053 5.39459ZM227.958 5.39459H228.175C228.195 5.39459 228.183 5.42675 228.15 5.4672L221.906 12.9114C221.873 12.9519 221.829 12.9851 221.809 12.9851H221.593C221.572 12.9851 221.583 12.9519 221.618 12.9114L227.861 5.4672C227.895 5.42675 227.938 5.39459 227.958 5.39459ZM225.863 5.39459H226.08C226.099 5.39459 226.088 5.42675 226.055 5.4672L219.811 12.9114C219.777 12.9519 219.734 12.9851 219.713 12.9851H219.496C219.477 12.9851 219.488 12.9519 219.521 12.9114L225.765 5.4672C225.8 5.42675 225.843 5.39459 225.863 5.39459ZM223.768 5.39459H223.984C224.004 5.39459 223.993 5.42675 223.96 5.4672L217.715 12.9114C217.681 12.9519 217.638 12.9851 217.618 12.9851H217.401C217.381 12.9851 217.393 12.9519 217.426 12.9114L223.67 5.4672C223.703 5.42675 223.747 5.39459 223.768 5.39459ZM221.672 5.39459H221.889C221.909 5.39459 221.898 5.42675 221.863 5.4672L215.62 12.9114C215.586 12.9519 215.542 12.9851 215.523 12.9851H215.306C215.286 12.9851 215.298 12.9519 215.331 12.9114L221.575 5.4672C221.608 5.42675 221.652 5.39459 221.672 5.39459ZM219.576 5.39459H219.793C219.814 5.39459 219.802 5.42675 219.768 5.4672L213.525 12.9114C213.491 12.9519 213.447 12.9851 213.428 12.9851H213.211C213.191 12.9851 213.201 12.9519 213.236 12.9114L219.48 5.4672C219.513 5.42675 219.557 5.39459 219.576 5.39459ZM217.481 5.39459H217.698C217.718 5.39459 217.707 5.42675 217.673 5.4672L211.429 12.9114C211.396 12.9519 211.352 12.9851 211.332 12.9851H211.116C211.095 12.9851 211.106 12.9519 211.14 12.9114L217.384 5.4672C217.418 5.42675 217.461 5.39459 217.481 5.39459ZM215.386 5.39459H215.603C215.622 5.39459 215.611 5.42675 215.578 5.4672L209.334 12.9114C209.3 12.9519 209.257 12.9851 209.236 12.9851H209.019C209 12.9851 209.011 12.9519 209.045 12.9114L215.288 5.4672C215.323 5.42675 215.366 5.39459 215.386 5.39459ZM213.291 5.39459H213.507C213.527 5.39459 213.516 5.42675 213.482 5.4672L207.238 12.9114C207.205 12.9519 207.161 12.9851 207.141 12.9851H206.924C206.904 12.9851 206.916 12.9519 206.949 12.9114L213.193 5.4672C213.227 5.42675 213.271 5.39459 213.291 5.39459ZM211.195 5.39459H211.412C211.432 5.39459 211.42 5.42675 211.386 5.4672L205.143 12.9114C205.109 12.9519 205.065 12.9851 205.046 12.9851H204.829C204.809 12.9851 204.821 12.9519 204.854 12.9114L211.098 5.4672C211.131 5.42675 211.175 5.39459 211.195 5.39459ZM209.099 5.39459H209.316C209.337 5.39459 209.325 5.42675 209.291 5.4672L203.048 12.9114C203.014 12.9519 202.97 12.9851 202.95 12.9851H202.734C202.714 12.9851 202.724 12.9519 202.759 12.9114L209.003 5.4672C209.036 5.42675 209.079 5.39459 209.099 5.39459ZM207.004 5.39459H207.221C207.24 5.39459 207.23 5.42675 207.196 5.4672L200.953 12.9114C200.918 12.9519 200.875 12.9851 200.855 12.9851H200.638C200.618 12.9851 200.629 12.9519 200.663 12.9114L206.906 5.4672C206.941 5.42675 206.984 5.39459 207.004 5.39459ZM204.909 5.39459H205.125C205.145 5.39459 205.134 5.42675 205.101 5.4672L198.856 12.9114C198.823 12.9519 198.78 12.9851 198.759 12.9851H198.543C198.522 12.9851 198.534 12.9519 198.568 12.9114L204.811 5.4672C204.845 5.42675 204.889 5.39459 204.909 5.39459ZM202.813 5.39459H203.03C203.05 5.39459 203.039 5.42675 203.005 5.4672L196.761 12.9114C196.728 12.9519 196.684 12.9851 196.664 12.9851H196.447C196.427 12.9851 196.439 12.9519 196.473 12.9114L202.716 5.4672C202.75 5.42675 202.794 5.39459 202.813 5.39459ZM200.718 5.39459H200.935C200.955 5.39459 200.943 5.42675 200.91 5.4672L194.666 12.9114C194.632 12.9519 194.588 12.9851 194.569 12.9851H194.352C194.332 12.9851 194.343 12.9519 194.377 12.9114L200.621 5.4672C200.654 5.42675 200.698 5.39459 200.718 5.39459ZM198.623 5.39459H198.839C198.86 5.39459 198.848 5.42675 198.814 5.4672L192.571 12.9114C192.537 12.9519 192.493 12.9851 192.473 12.9851H192.257C192.237 12.9851 192.248 12.9519 192.281 12.9114L198.526 5.4672C198.559 5.42675 198.602 5.39459 198.623 5.39459ZM196.527 5.39459H196.744C196.764 5.39459 196.753 5.42675 196.719 5.4672L190.476 12.9114C190.441 12.9519 190.398 12.9851 190.378 12.9851H190.161C190.142 12.9851 190.152 12.9519 190.186 12.9114L196.43 5.4672C196.464 5.42675 196.507 5.39459 196.527 5.39459ZM194.432 5.39459H194.648C194.668 5.39459 194.658 5.42675 194.623 5.4672L188.379 12.9114C188.346 12.9519 188.303 12.9851 188.283 12.9851H188.066C188.045 12.9851 188.057 12.9519 188.091 12.9114L194.334 5.4672C194.368 5.42675 194.412 5.39459 194.432 5.39459ZM192.336 5.39459H192.553C192.573 5.39459 192.561 5.42675 192.528 5.4672L186.284 12.9114C186.251 12.9519 186.207 12.9851 186.187 12.9851H185.97C185.95 12.9851 185.962 12.9519 185.996 12.9114L192.239 5.4672C192.273 5.42675 192.317 5.39459 192.336 5.39459Z"
                    fill="#89909D"
                  />
                  <path
                    d="M164.086 2.38666H164.303C164.323 2.38666 164.312 2.41881 164.278 2.45926L158.034 9.90349C158.001 9.94395 157.957 9.97714 157.938 9.97714H157.721C157.7 9.97714 157.711 9.94395 157.746 9.90349L163.989 2.45926C164.023 2.41881 164.067 2.38666 164.086 2.38666ZM161.991 2.38666H162.208C162.228 2.38666 162.216 2.41881 162.183 2.45926L155.939 9.90349C155.906 9.94395 155.862 9.97714 155.841 9.97714H155.625C155.605 9.97714 155.616 9.94395 155.65 9.90349L161.894 2.45926C161.928 2.41881 161.971 2.38666 161.991 2.38666ZM159.896 2.38666H160.113C160.132 2.38666 160.121 2.41881 160.088 2.45926L153.844 9.90349C153.809 9.94395 153.766 9.97714 153.746 9.97714H153.529C153.51 9.97714 153.521 9.94395 153.554 9.90349L159.798 2.45926C159.833 2.41881 159.876 2.38666 159.896 2.38666ZM157.801 2.38666H158.017C158.037 2.38666 158.026 2.41881 157.991 2.45926L151.748 9.90349C151.714 9.94395 151.671 9.97714 151.651 9.97714H151.434C151.414 9.97714 151.426 9.94395 151.459 9.90349L157.703 2.45926C157.736 2.41881 157.78 2.38666 157.801 2.38666ZM155.704 2.38666H155.921C155.942 2.38666 155.931 2.41881 155.896 2.45926L149.653 9.90349C149.619 9.94395 149.575 9.97714 149.556 9.97714H149.339C149.319 9.97714 149.33 9.94395 149.364 9.90349L155.608 2.45926C155.641 2.41881 155.685 2.38666 155.704 2.38666ZM153.609 2.38666H153.826C153.846 2.38666 153.835 2.41881 153.801 2.45926L147.558 9.90349C147.524 9.94395 147.48 9.97714 147.46 9.97714H147.244C147.223 9.97714 147.234 9.94395 147.269 9.90349L153.512 2.45926C153.546 2.41881 153.589 2.38666 153.609 2.38666ZM151.514 2.38666H151.731C151.75 2.38666 151.739 2.41881 151.706 2.45926L145.462 9.90349C145.429 9.94395 145.385 9.97714 145.365 9.97714H145.148C145.128 9.97714 145.139 9.94395 145.173 9.90349L151.416 2.45926C151.451 2.41881 151.494 2.38666 151.514 2.38666ZM149.419 2.38666H149.636C149.655 2.38666 149.644 2.41881 149.611 2.45926L143.366 9.90349C143.333 9.94395 143.29 9.97714 143.269 9.97714H143.052C143.033 9.97714 143.044 9.94395 143.078 9.90349L149.321 2.45926C149.355 2.41881 149.399 2.38666 149.419 2.38666ZM147.324 2.38666H147.54C147.56 2.38666 147.549 2.41881 147.515 2.45926L141.271 9.90349C141.237 9.94395 141.193 9.97714 141.174 9.97714H140.957C140.937 9.97714 140.949 9.94395 140.982 9.90349L147.226 2.45926C147.259 2.41881 147.303 2.38666 147.324 2.38666ZM145.228 2.38666H145.445C145.465 2.38666 145.453 2.41881 145.419 2.45926L139.176 9.90349C139.142 9.94395 139.098 9.97714 139.079 9.97714H138.862C138.842 9.97714 138.853 9.94395 138.887 9.90349L145.131 2.45926C145.164 2.41881 145.208 2.38666 145.228 2.38666ZM143.132 2.38666H143.349C143.37 2.38666 143.358 2.41881 143.324 2.45926L137.081 9.90349C137.047 9.94395 137.003 9.97714 136.983 9.97714H136.767C136.747 9.97714 136.757 9.94395 136.791 9.90349L143.035 2.45926C143.069 2.41881 143.112 2.38666 143.132 2.38666ZM141.037 2.38666H141.254C141.273 2.38666 141.263 2.41881 141.229 2.45926L134.985 9.90349C134.951 9.94395 134.908 9.97714 134.888 9.97714H134.671C134.651 9.97714 134.662 9.94395 134.696 9.90349L140.939 2.45926C140.974 2.41881 141.017 2.38666 141.037 2.38666ZM138.942 2.38666H139.158C139.178 2.38666 139.167 2.41881 139.134 2.45926L132.889 9.90349C132.856 9.94395 132.813 9.97714 132.792 9.97714H132.575C132.555 9.97714 132.567 9.94395 132.601 9.90349L138.844 2.45926C138.878 2.41881 138.922 2.38666 138.942 2.38666ZM136.846 2.38666H137.063C137.083 2.38666 137.072 2.41881 137.038 2.45926L130.794 9.90349C130.76 9.94395 130.716 9.97714 130.697 9.97714H130.48C130.46 9.97714 130.472 9.94395 130.505 9.90349L136.749 2.45926C136.783 2.41881 136.827 2.38666 136.846 2.38666ZM134.751 2.38666H134.968C134.988 2.38666 134.976 2.41881 134.942 2.45926L128.699 9.90349C128.665 9.94395 128.621 9.97714 128.601 9.97714H128.385C128.365 9.97714 128.376 9.94395 128.41 9.90349L134.654 2.45926C134.687 2.41881 134.73 2.38666 134.751 2.38666ZM132.655 2.38666H132.872C132.893 2.38666 132.881 2.41881 132.847 2.45926L126.604 9.90349C126.57 9.94395 126.526 9.97714 126.506 9.97714H126.289C126.27 9.97714 126.28 9.94395 126.314 9.90349L132.559 2.45926C132.592 2.41881 132.635 2.38666 132.655 2.38666ZM130.56 2.38666H130.777C130.796 2.38666 130.786 2.41881 130.752 2.45926L124.509 9.90349C124.474 9.94395 124.431 9.97714 124.411 9.97714H124.194C124.174 9.97714 124.185 9.94395 124.219 9.90349L130.462 2.45926C130.497 2.41881 130.54 2.38666 130.56 2.38666ZM128.465 2.38666H128.681C128.701 2.38666 128.69 2.41881 128.656 2.45926L122.412 9.90349C122.379 9.94395 122.336 9.97714 122.316 9.97714H122.099C122.078 9.97714 122.09 9.94395 122.124 9.90349L128.367 2.45926C128.401 2.41881 128.445 2.38666 128.465 2.38666ZM126.369 2.38666H126.586C126.606 2.38666 126.594 2.41881 126.561 2.45926L120.317 9.90349C120.284 9.94395 120.24 9.97714 120.22 9.97714H120.003C119.983 9.97714 119.994 9.94395 120.029 9.90349L126.272 2.45926C126.306 2.41881 126.35 2.38666 126.369 2.38666ZM124.274 2.38666H124.491C124.511 2.38666 124.499 2.41881 124.466 2.45926L118.222 9.90349C118.188 9.94395 118.144 9.97714 118.124 9.97714H117.908C117.888 9.97714 117.899 9.94395 117.932 9.90349L124.177 2.45926C124.21 2.41881 124.253 2.38666 124.274 2.38666ZM122.179 2.38666H122.396C122.415 2.38666 122.404 2.41881 122.37 2.45926L116.127 9.90349C116.092 9.94395 116.049 9.97714 116.029 9.97714H115.812C115.793 9.97714 115.804 9.94395 115.837 9.90349L122.081 2.45926C122.115 2.41881 122.158 2.38666 122.179 2.38666ZM120.083 2.38666H120.299C120.32 2.38666 120.309 2.41881 120.275 2.45926L114.031 9.90349C113.997 9.94395 113.954 9.97714 113.934 9.97714H113.717C113.697 9.97714 113.708 9.94395 113.742 9.90349L119.985 2.45926C120.019 2.41881 120.063 2.38666 120.083 2.38666ZM117.987 2.38666H118.204C118.224 2.38666 118.214 2.41881 118.179 2.45926L111.935 9.90349C111.902 9.94395 111.858 9.97714 111.839 9.97714H111.622C111.601 9.97714 111.613 9.94395 111.647 9.90349L117.89 2.45926C117.924 2.41881 117.968 2.38666 117.987 2.38666ZM115.892 2.38666H116.109C116.129 2.38666 116.117 2.41881 116.084 2.45926L109.84 9.90349C109.807 9.94395 109.763 9.97714 109.742 9.97714H109.526C109.506 9.97714 109.517 9.94395 109.552 9.90349L115.795 2.45926C115.829 2.41881 115.873 2.38666 115.892 2.38666Z"
                    fill="#89909D"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M1.07455 11.0984H160.29C160.348 11.0984 160.375 11.1212 160.351 11.1503L159.48 12.1875C159.456 12.2165 159.391 12.2393 159.333 12.2393H0.117187C0.0601395 12.2393 0.0331714 12.2165 0.0570278 12.1875L0.928304 11.1503C0.952161 11.1212 1.01751 11.0984 1.07455 11.0984Z"
                    fill="#89909D"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M168.717 0.085022H181.149C181.166 0.085022 181.12 0.158666 181.044 0.248905L171.121 12.0754C171.044 12.1657 170.968 12.2393 170.95 12.2393H158.517C158.5 12.2393 158.546 12.1657 158.622 12.0754L168.545 0.248905C168.621 0.158666 168.698 0.085022 168.717 0.085022Z"
                    fill="#89909D"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M191.614 3.11163H350.831C350.888 3.11163 350.915 3.13445 350.891 3.1635L350.02 4.20073C349.996 4.22977 349.931 4.25259 349.873 4.25259H190.657C190.6 4.25259 190.573 4.22977 190.597 4.20073L191.468 3.1635C191.492 3.13445 191.557 3.11163 191.614 3.11163Z"
                    fill={brandColor}
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M180.022 3.09302H192.455C192.472 3.09302 192.426 3.16666 192.35 3.2569L182.427 15.0834C182.35 15.1737 182.274 15.2473 182.256 15.2473H169.823C169.806 15.2473 169.852 15.1737 169.928 15.0834L179.851 3.2569C179.927 3.16666 180.004 3.09302 180.022 3.09302Z"
                    fill={brandColor}
                  />
                </g>
                <defs>
                  <clipPath id="clip0_6314_4743">
                    <rect width="351" height="15.2473" fill="white" />
                  </clipPath>
                </defs>
              </svg>
              {clientDetails && (
                <SenderAndBillingDetails
                  isBillingDetails={true}
                  colorName={brandColor}
                  clientDetails={clientDetails}
                  companyDetails={companyDetails}
                  issueDate={issueDate}
                  dueDate={dueDate}
                />
              )}

              {jobDescription && jobDescription?.length > 0 && (
                <>
                  <div className="fw-medium text-black2d font-primary mb-2 mt-4">
                    <span style={{ color: brandColor }}> Job Description</span>
                  </div>
                  <p
                    className="text-black2D font-primary fw-normal fs-9"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {jobDescription}
                  </p>
                </>
              )}
            </>
          )}

          {/* TABLE AREA */}
          {selectedTab === "2" && (
            <>
              <div className="fw-medium text-black2d font-primary mt-4">
                <span style={{ color: brandColor }}>Items Summary</span>
              </div>
              <hr className=" text-greyE4 border-2" />
              {(mainTableData.length > 0 || allViewData.length > 0) && (
                <>
                  {selectedOption === "by-category-subcategory-item" ? (
                    <>
                      {allViewData.map((cat) => (
                        <div key={cat.categoryId} className="mt-4">
                          {/* Category heading like screenshot (MATERIAL, SERVICES, etc.) */}
                          <div
                            className="fw-semibold text-uppercase fs-14 "
                            style={{ color: brandColor }}
                          >
                            {cat.category}
                          </div>

                          <ItemsTable
                            colorName={brandColor}
                            columns={columns}
                            data={cat.rows}
                          />
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="mt-3">
                      <ItemsTable
                        colorName={brandColor}
                        columns={columns}
                        data={mainTableData}
                      />
                    </div>
                  )}

                  <InvoiceFooter colorName={brandColor} />
                </>
              )}
            </>
          )}

          {selectedTab === "3" && (
            <>
              <div className="fw-medium text-black2d font-primary mt-4">
                <span style={{ color: brandColor }}>Quote Summary</span>
              </div>
              <hr className="mb-4 text-greyE4 border-2" />
              {allSelectedSections &&
                allSelectedSections.length > 0 &&
                allSelectedSections.map((section, index) => (
                  <div key={section.id} className="mb-4">
                    <div
                      className="fw-semibold text-uppercase fs-14 mb-2"
                      style={{ color: brandColor }}
                    >
                      {index + 1}. {section.title}
                    </div>
                    <div className="html-content">
                      {parse(section.content) ?? "-"}
                    </div>
                  </div>
                ))}
            </>
          )}
        </div>

        {/* SVG at bottom */}
        <svg
          className="w-100"
          viewBox="0 0 319 4"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="319" height="4" fill="#EEEEEE" />
          <rect x="106.333" width="106.333" height="4" fill={brandColor} />
        </svg>
      </div>
    </Col>
  );
};

export default TemplateThird;
